import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiasApi from 'config/api/dpias/dpias';
import type { DpiaDetail, DpiaRisk } from 'config/api/dpias/dpias.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import { invalidateOwnerAssetRopaQueries } from 'pages/ProcessingActivities/_shared/invalidateOwnerAssetRopaQueries';
import {
  catchHttpStatuses,
  getUnavailableStatus,
  HTTP_UNAVAILABLE_STATUSES,
  isUnavailableQueryResult,
  unwrapAvailableQueryData,
} from 'pages/ProcessingActivities/_shared/queryUnavailable';
import useDpiaEditorContext from 'pages/ProcessingActivities/_shared/useDpiaEditorContext';
import userDataStorage from 'storages/userDataStorage';

import {
  applyMeasureResponsibleDefaults,
  mapDetailToFormValues,
  mapFormValuesToUpdateInput,
  resolveDpiaMeasureResponsible,
  type OwnerDpiaFormValues,
} from './ownerDpiaEditor.helpers';

type Params = {
  dpiaId: number;
  onConflict?: () => void;
  onNavigate: () => void;
  onSaveSuccess?: (closeAfterSave: boolean) => void;
  onSubmitSuccess?: () => void;
  organizationId?: number;
};

const isTemporaryId = (id: number) => id < 0;

const applyRevisionFromResponse = (currentRevision: number, revision: number | null): number =>
  revision == null ? currentRevision : revision;

type SyncNestedRisksAndMeasuresResult = {
  revision: number;
  hadNestedMutation: boolean;
};

const syncNestedRisksAndMeasures = async (
  organizationId: number,
  dpiaId: number,
  oldRisks: DpiaRisk[],
  newRisks: DpiaRisk[],
  currentRevision: number,
): Promise<SyncNestedRisksAndMeasuresResult> => {
  let revision = currentRevision;
  let hadNestedMutation = false;

  const trackRevision = (responseRevision: number | null) => {
    hadNestedMutation = true;
    revision = applyRevisionFromResponse(revision, responseRevision);
  };

  const risksToDelete = oldRisks.filter(oldRisk => !newRisks.some(newRisk => newRisk.id === oldRisk.id));
  const deletedRiskIds = new Set(risksToDelete.map(risk => risk.id));
  if (risksToDelete.length > 0) {
    hadNestedMutation = true;
    await Promise.all(risksToDelete.map(risk => dpiasApi.deleteRisk(organizationId, dpiaId, risk.id)));
  }

  const knownRiskIds = new Set(oldRisks.map(risk => risk.id).filter(riskId => !isTemporaryId(riskId) && !deletedRiskIds.has(riskId)));

  // Sequential creates so temporary measure ids can reference the new risk id.
  // eslint-disable-next-line no-restricted-syntax
  for (const newRisk of newRisks) {
    const isNewRisk = isTemporaryId(newRisk.id);
    let realRiskId = newRisk.id;

    if (isNewRisk) {
      // eslint-disable-next-line no-await-in-loop
      const createdRiskResponse = await dpiasApi.createRisk(
        organizationId,
        dpiaId,
        {
          title: newRisk.title,
          description: newRisk.description,
          affectedRightsAndFreedoms: newRisk.affectedRightsAndFreedoms,
          likelihood: newRisk.likelihood,
          impact: newRisk.impact,
          sortOrder: newRisk.sortOrder,
        },
        { knownRiskIds: Array.from(knownRiskIds) },
      );
      trackRevision(createdRiskResponse.revision);
      realRiskId = createdRiskResponse.data.id;
      knownRiskIds.add(realRiskId);
    } else {
      const oldRisk = oldRisks.find(risk => risk.id === newRisk.id);
      const needsUpdate =
        !oldRisk ||
        oldRisk.title !== newRisk.title ||
        oldRisk.description !== newRisk.description ||
        oldRisk.affectedRightsAndFreedoms !== newRisk.affectedRightsAndFreedoms ||
        oldRisk.likelihood !== newRisk.likelihood ||
        oldRisk.impact !== newRisk.impact ||
        oldRisk.sortOrder !== newRisk.sortOrder;

      if (needsUpdate) {
        // eslint-disable-next-line no-await-in-loop
        const updatedRiskResponse = await dpiasApi.updateRisk(organizationId, dpiaId, realRiskId, {
          title: newRisk.title,
          description: newRisk.description,
          affectedRightsAndFreedoms: newRisk.affectedRightsAndFreedoms,
          likelihood: newRisk.likelihood,
          impact: newRisk.impact,
          sortOrder: newRisk.sortOrder,
        });
        trackRevision(updatedRiskResponse.revision);
      }
    }

    const oldMeasures = isNewRisk ? [] : oldRisks.find(risk => risk.id === newRisk.id)?.measures || [];
    const newMeasures = newRisk.measures;

    const measuresToDelete = oldMeasures.filter(oldMeasure => !newMeasures.some(newMeasure => newMeasure.id === oldMeasure.id));
    if (measuresToDelete.length > 0) {
      hadNestedMutation = true;
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(measuresToDelete.map(measure => dpiasApi.deleteMeasure(organizationId, dpiaId, measure.id)));
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const newMeasure of newMeasures) {
      if (isTemporaryId(newMeasure.id)) {
        // eslint-disable-next-line no-await-in-loop
        const createdMeasureResponse = await dpiasApi.createMeasure(organizationId, dpiaId, {
          dpiaRiskId: realRiskId,
          title: newMeasure.title,
          description: newMeasure.description,
          responsibleMembershipId: newMeasure.responsibleMembershipId,
          dueDate: newMeasure.dueDate,
          status: newMeasure.status,
          sortOrder: newMeasure.sortOrder,
        });
        trackRevision(createdMeasureResponse.revision);
      } else {
        const oldMeasure = oldMeasures.find(measure => measure.id === newMeasure.id);
        const needsUpdate =
          !oldMeasure ||
          oldMeasure.title !== newMeasure.title ||
          oldMeasure.description !== newMeasure.description ||
          oldMeasure.responsibleMembershipId !== newMeasure.responsibleMembershipId ||
          oldMeasure.dueDate !== newMeasure.dueDate ||
          oldMeasure.status !== newMeasure.status ||
          oldMeasure.sortOrder !== newMeasure.sortOrder;

        if (needsUpdate) {
          // eslint-disable-next-line no-await-in-loop
          const updatedMeasureResponse = await dpiasApi.updateMeasure(organizationId, dpiaId, newMeasure.id, {
            title: newMeasure.title,
            description: newMeasure.description,
            responsibleMembershipId: newMeasure.responsibleMembershipId,
            dueDate: newMeasure.dueDate,
            status: newMeasure.status,
            sortOrder: newMeasure.sortOrder,
          });
          trackRevision(updatedMeasureResponse.revision);
        }
      }
    }
  }

  if (hadNestedMutation && revision === currentRevision) {
    const {
      data: {
        dpia: { revision: refreshedRevision },
      },
    } = await dpiasApi.getDetail(organizationId, dpiaId);
    revision = refreshedRevision;
  }

  return { revision, hadNestedMutation };
};

const persistDpiaDraft = async (organizationId: number, dpiaId: number, currentDetail: DpiaDetail, formValues: OwnerDpiaFormValues) => {
  const { revision, hadNestedMutation } = await syncNestedRisksAndMeasures(
    organizationId,
    dpiaId,
    currentDetail.risks,
    formValues.risks,
    currentDetail.revision,
  );

  const { data: patchData } = await dpiasApi.update(organizationId, dpiaId, mapFormValuesToUpdateInput(revision, formValues));

  if (!hadNestedMutation) {
    return patchData;
  }

  const { data } = await dpiasApi.getDetail(organizationId, dpiaId);
  return data;
};

const useOwnerDpiaEditor = ({ dpiaId, onConflict, onNavigate, onSaveSuccess, onSubmitSuccess, organizationId }: Params) => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<OwnerDpiaFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const detailQuery = useQuery(
    [QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await dpiasApi.getDetail(organizationId as number, dpiaId);
        return data;
      }, HTTP_UNAVAILABLE_STATUSES),
    {
      enabled: Boolean(organizationId && dpiaId),
      retry: false,
    },
  );

  const envelope = unwrapAvailableQueryData(detailQuery.data);
  const isUnavailable = isUnavailableQueryResult(detailQuery.data);
  const unavailableStatus = getUnavailableStatus(detailQuery.data);
  const { bannerMeta, context, isContextLoading, operationalOwnerMembershipId, operationalOwnerName, sourceTrigger } = useDpiaEditorContext(
    { envelope, organizationId },
  );

  const userData = userDataStorage.get() || {};
  const currentUserName = [userData.first_name, userData.last_name].filter(Boolean).join(' ');

  const measureResponsibleDefault = useMemo(
    () =>
      resolveDpiaMeasureResponsible({
        currentUserMembershipId: typeof userData.id === 'number' ? userData.id : undefined,
        currentUserName,
        detailResponsibleMembershipId: envelope?.dpia?.responsibleMembershipId,
        detailResponsibleName: envelope?.dpia?.responsibleName,
        operationalOwnerMembershipId,
        operationalOwnerName,
      }),
    [
      currentUserName,
      envelope?.dpia?.responsibleMembershipId,
      envelope?.dpia?.responsibleName,
      operationalOwnerMembershipId,
      operationalOwnerName,
      userData.id,
    ],
  );

  useEffect(() => {
    if (!envelope?.dpia) {
      return;
    }

    setFormValues(applyMeasureResponsibleDefaults(mapDetailToFormValues(envelope.dpia), measureResponsibleDefault));
    setIsDirty(false);
    setValidationErrors({});
  }, [envelope?.dpia]);

  useEffect(() => {
    if (!measureResponsibleDefault) {
      return;
    }

    setFormValues(current => (current ? applyMeasureResponsibleDefaults(current, measureResponsibleDefault) : current));
  }, [measureResponsibleDefault]);

  const handleConflictError = useCallback(
    async (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        await queryClient.invalidateQueries([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId]);
        onConflict?.();
      }
    },
    [dpiaId, onConflict, organizationId, queryClient],
  );

  const allowNextNavigationRef = useRef(() => {});

  const saveMutation = useMutation(
    MUTATION_KEYS.DPIA_UPDATE,
    async (closeAfterSave: boolean) => {
      if (!formValues || !envelope?.dpia || !organizationId) return null;

      const data = await persistDpiaDraft(organizationId, dpiaId, envelope.dpia, formValues);
      return { closeAfterSave, data };
    },
    {
      onSuccess: result => {
        if (!result) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], result.data);
        setFormValues(applyMeasureResponsibleDefaults(mapDetailToFormValues(result.data.dpia), measureResponsibleDefault));
        setIsDirty(false);
        if (result.closeAfterSave) {
          allowNextNavigationRef.current();
        }
        onSaveSuccess?.(result.closeAfterSave);
      },
      onError: (error: unknown) => {
        handleConflictError(error);
      },
    },
  );

  const submitMutation = useMutation(
    MUTATION_KEYS.DPIA_SUBMIT,
    async () => {
      if (!envelope?.dpia || !formValues || !organizationId) return null;

      let currentDetail = envelope.dpia;

      if (isDirty) {
        const data = await persistDpiaDraft(organizationId, dpiaId, currentDetail, formValues);
        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
        currentDetail = data.dpia;
      }

      const { data } = await dpiasApi.submit(organizationId, dpiaId, { revision: currentDetail.revision });
      return data;
    },
    {
      onSuccess: data => {
        if (!data) return;

        queryClient.setQueryData([QUERY_KEYS.DPIA_DETAIL, organizationId, dpiaId], data);
        setFormValues(applyMeasureResponsibleDefaults(mapDetailToFormValues(data.dpia), measureResponsibleDefault));
        setIsDirty(false);
        allowNextNavigationRef.current();
        invalidateOwnerAssetRopaQueries(queryClient, organizationId);
        onSubmitSuccess?.();
      },
      onError: (error: unknown) => {
        handleConflictError(error);
      },
    },
  );

  const patchFormValues = useCallback((patch: Partial<OwnerDpiaFormValues>) => {
    setFormValues(current => (current ? { ...current, ...patch } : current));
    setIsDirty(true);
    setValidationErrors({});
  }, []);

  const handleSaveClick = useCallback(
    async (closeAfterSave: boolean) => {
      await saveMutation.mutateAsync(closeAfterSave);
    },
    [saveMutation],
  );

  const handleSubmit = useCallback(async () => {
    await submitMutation.mutateAsync();
  }, [submitMutation]);

  const { NavigationPrompt, allowNextNavigation, handleNavigation } = useUnsavedChangesProtection({
    enabled: Boolean(envelope?.dpia.canEdit),
    isDirty,
    onNavigate,
    onSave: async () => {
      await saveMutation.mutateAsync(false);
    },
  });

  allowNextNavigationRef.current = allowNextNavigation;

  return useMemo(
    () => ({
      bannerMeta,
      context,
      detail: envelope?.dpia,
      measureResponsibleDefault,
      formValues,
      handleNavigation,
      handleSave: handleSaveClick,
      handleSubmit,
      isDirty,
      isError: detailQuery.isError,
      isContextLoading,
      isLoading: detailQuery.isLoading,
      isUnavailable,
      unavailableStatus,
      isSaving: saveMutation.isLoading,
      isSubmitting: submitMutation.isLoading,
      NavigationPrompt,
      patchFormValues,
      refetch: detailQuery.refetch,
      saveMutation,
      setFormValues,
      setIsDirty,
      setValidationErrors,
      sourceTrigger,
      submitMutation,
      validationErrors,
    }),
    [
      bannerMeta,
      context,
      detailQuery.isError,
      measureResponsibleDefault,
      envelope,
      detailQuery.isLoading,
      detailQuery.refetch,
      formValues,
      handleNavigation,
      handleSaveClick,
      handleSubmit,
      isContextLoading,
      isDirty,
      isUnavailable,
      NavigationPrompt,
      patchFormValues,
      saveMutation,
      submitMutation,
      sourceTrigger,
      unavailableStatus,
      validationErrors,
    ],
  );
};

export default useOwnerDpiaEditor;
